import { Fragment } from 'react';
import { MailCheckIcon } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from 'src/lib/utils';

import { useUsers } from '../model';

import AddUser from './AddUser';
import EditUser from './EditUser';
import InvitationConfirmed from './InvitationConfirmed';
import RemoveDialog from './RemoveDialog';
import TransferOwnershipDialog from './TransferOwnershipDialog';

const actionMap = {
  add: {
    icon: null,
    title: 'Invite a member to your organization',
    description: "They'll receive an email with instructions to set up their account.",
    component: AddUser,
    modalWidth: 'sm:max-w-xl',
  },
  edit: {
    icon: null,
    title: 'Edit team member details',
    description:
      "You can update this member's name, phone number, and role. Only they can change their email address from their profile.",
    component: EditUser,
    modalWidth: 'sm:max-w-xl',
  },
  'invitation-sent': {
    icon: MailCheckIcon,
    title: 'Invitation sent!',
    description: "We've sent them an email with instructions to set up their Codekeeper account.",
    component: InvitationConfirmed,
    modalWidth: 'sm:max-w-[425px]',
  },
};

const UserDialogManager = () => {
  const {
    ui: { dialogOpen, closeDialog, type },
  } = useUsers();

  const handleOpenChange = (open: boolean) => {
    if (!open) closeDialog();
  };

  const action = type ? actionMap[type as keyof typeof actionMap] : null;
  const Icon = action?.icon;

  return (
    <Fragment>
      <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
        <DialogTrigger />
        <DialogContent className={cn('gap-0', action?.modalWidth)}>
          <DialogHeader className={cn('mb-6 gap-1.5', type === 'invitation-sent' && 'items-center text-center mb-4')}>
            {Icon && <Icon className="size-8 mb-2.5" />}
            <DialogTitle className={cn('text-base font-bold', type === 'invitation-sent' && 'font-semibold text-lg')}>
              {action?.title}
            </DialogTitle>
            <DialogDescription>{action?.description}</DialogDescription>
          </DialogHeader>
          {action?.component && <action.component />}
        </DialogContent>
      </Dialog>

      <RemoveDialog />

      <TransferOwnershipDialog />
    </Fragment>
  );
};

export default UserDialogManager;
